// Three-track architecture (Residential · Commercial · Industrial).
// Single source of truth for per-track navigation, paths, and splash content.
//
// Routing decision (see REBUILD_PLAN.md §4): the splash lives at "/", the
// residential HOME lives at "/residential", and residential deep pages KEEP
// their existing top-level paths (/services/*, /gallery, /about, /contact,
// /service-area, /blog, /reviews) to preserve already-indexed SEO equity.
// Commercial and industrial are new nested sections under /commercial and
// /industrial. The header reads getTrack(pathname) to render the right nav.

import { SERVICES } from "@/lib/constants";

export const TRACK_KEYS = ["residential", "commercial", "industrial"];

// Residential services = existing 6 + the NEW masonry page.
const RESIDENTIAL_SERVICES = [
    ...SERVICES,
    { label: "Masonry", path: "/services/masonry" },
];

const COMMERCIAL_SERVICES = [
    { label: "Fencing & Gates", path: "/commercial/services/commercial-fencing-gates" },
    { label: "Parking Lots & Asphalt", path: "/commercial/services/parking-lots-asphalt" },
    { label: "Concrete & Sidewalks", path: "/commercial/services/concrete-sidewalks" },
    { label: "Interlocking & Hardscape", path: "/commercial/services/interlocking-hardscape" },
    { label: "Masonry & Restoration", path: "/commercial/services/commercial-masonry-restoration" },
    { label: "Landscaping & Grounds", path: "/commercial/services/landscaping-grounds" },
    { label: "Snow & Ice Management", path: "/commercial/services/snow-ice-management" },
    { label: "Exterior Build-Outs", path: "/commercial/services/exterior-renovations-buildouts" },
];

const INDUSTRIAL_SERVICES = [
    { label: "Heavy-Duty Asphalt & Paving", path: "/industrial/services/heavy-duty-asphalt-paving" },
    { label: "Industrial Concrete", path: "/industrial/services/industrial-concrete" },
    { label: "Site Security Fencing", path: "/industrial/services/site-security-fencing" },
    { label: "Masonry & Structural Repair", path: "/industrial/services/industrial-masonry-structural" },
    { label: "Retaining Walls & Site Works", path: "/industrial/services/retaining-walls-site-works" },
    { label: "Bollards & Traffic Safety", path: "/industrial/services/bollards-guard-rails-safety" },
    { label: "Grounds & Drainage", path: "/industrial/services/exterior-grounds-drainage" },
    { label: "Snow & Ice Management", path: "/industrial/services/snow-ice-management-industrial" },
];

export const TRACKS = {
    residential: {
        key: "residential",
        label: "Residential",
        home: "/residential",
        servicesIndex: "/services",
        servicesBase: "/services",
        contact: "/contact",
        blog: "/blog",
        gallery: "/gallery",
        about: "/about",
        serviceArea: "/service-area",
        services: RESIDENTIAL_SERVICES,
        navLinks: [
            { label: "Gallery", path: "/gallery" },
            { label: "Blog", path: "/blog" },
            { label: "About", path: "/about" },
            { label: "Service Area", path: "/service-area" },
            { label: "Contact", path: "/contact" },
        ],
        // Splash card content
        eyebrow: "Residential",
        cardTitle: "Homes, Backyards & Renovations",
        cardBlurb: "Fencing, decks, interlocking, concrete, landscaping, renovations & masonry for GTA homeowners.",
        // Real Reformity project photo (residential)
        image: "/projects/decking/carport-carporteg2.webp",
        // Audience phrasing used by shared components
        audienceLabel: "Homeowners",
        audienceLine: "See what homeowners say.",
        audienceEyebrow: "Real Homeowners",
    },
    commercial: {
        key: "commercial",
        label: "Commercial",
        home: "/commercial",
        servicesIndex: "/commercial/services",
        servicesBase: "/commercial/services",
        contact: "/commercial/contact",
        blog: "/commercial/blog",
        gallery: "/commercial/gallery",
        about: "/commercial/about",
        serviceArea: "/commercial/service-area",
        services: COMMERCIAL_SERVICES,
        navLinks: [
            { label: "Gallery", path: "/commercial/gallery" },
            { label: "Blog", path: "/commercial/blog" },
            { label: "About", path: "/commercial/about" },
            { label: "Service Area", path: "/commercial/service-area" },
            { label: "Contact", path: "/commercial/contact" },
        ],
        eyebrow: "Commercial",
        cardTitle: "Retail, Office, Multi-Unit & Property Management",
        cardBlurb: "Insured, scheduled, COI-ready exterior & property contracting for owners, developers and property managers.",
        image: "/images/commercial/commercial-track-hero.4b330f12.webp",
        audienceLabel: "Property Owners & Managers",
        audienceLine: "What property owners & managers value.",
        audienceEyebrow: "Trusted by Property Teams",
    },
    industrial: {
        key: "industrial",
        label: "Industrial",
        home: "/industrial",
        servicesIndex: "/industrial/services",
        servicesBase: "/industrial/services",
        contact: "/industrial/contact",
        blog: "/industrial/blog",
        gallery: "/industrial/gallery",
        about: "/industrial/about",
        serviceArea: "/industrial/service-area",
        services: INDUSTRIAL_SERVICES,
        navLinks: [
            { label: "Gallery", path: "/industrial/gallery" },
            { label: "Blog", path: "/industrial/blog" },
            { label: "About", path: "/industrial/about" },
            { label: "Service Area", path: "/industrial/service-area" },
            { label: "Contact", path: "/industrial/contact" },
        ],
        eyebrow: "Industrial",
        cardTitle: "Warehouses, Plants, Yards & Facilities",
        cardBlurb: "Heavy-duty, durable, compliant exterior & site work for warehouses, plants and logistics facilities.",
        image: "/images/industrial/industrial-track-hero.ddc2fc82.webp",
        audienceLabel: "Facility & Asset Managers",
        audienceLine: "What facility & asset managers value.",
        audienceEyebrow: "Built for Industrial Sites",
    },
};

export const TRACK_LIST = TRACK_KEYS.map((k) => TRACKS[k]);

/** Resolve the active track key from a pathname. Residential is the default. */
export function getTrackKeyFromPath(pathname = "/") {
    if (pathname.startsWith("/commercial")) return "commercial";
    if (pathname.startsWith("/industrial")) return "industrial";
    return "residential";
}

/** Resolve the active track config object from a pathname. */
export function getTrack(pathname = "/") {
    return TRACKS[getTrackKeyFromPath(pathname)];
}

// --- preferredTrack cookie (never traps; always re-selectable on the splash) ---
export const PREFERRED_TRACK_COOKIE = "preferredTrack";
const PREFERRED_TRACK_MAX_AGE = 60 * 60 * 24 * 180; // 180 days

export function setPreferredTrack(key) {
    if (typeof document === "undefined" || !TRACK_KEYS.includes(key)) return;
    document.cookie = `${PREFERRED_TRACK_COOKIE}=${key}; path=/; max-age=${PREFERRED_TRACK_MAX_AGE}; SameSite=Lax`;
}

export function getPreferredTrack() {
    if (typeof document === "undefined") return null;
    const match = document.cookie.match(
        new RegExp(`(?:^|; )${PREFERRED_TRACK_COOKIE}=([^;]+)`),
    );
    return match && TRACK_KEYS.includes(match[1]) ? match[1] : null;
}
